import { format, formatDistanceToNow } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { AgentVerificationLog, AgentVerificationLogType } from '@percent/admin-dashboard/api/types'

const REUSED_AGENT_VERIFICATION_LOG_TYPES: readonly AgentVerificationLogType[] = [
  'found_email_previous_approval',
  'found_email_previous_approval_from_different_partner'
]

type AgentVerificationLogData = {
  accountEmail?: string
  kycNotRequiredReason?: 'allow_list' | 'risk_level'
  domain?: string
  expiresAt?: string
  blockPolicy?:
    | 'applied_for_different_countries'
    | 'high_risk_tld'
    | 'headless_user_agent'
    | 'auto_rejection_list'
    | 'alias_email'
}

export const useAgentVerificationTimeline = (logs: AgentVerificationLog[] | null) => {
  const { t } = useTranslation()

  const getDescription = (log: AgentVerificationLog<AgentVerificationLogData>) => {
    switch (true) {
      case typeof log.data?.accountEmail === 'string':
        return t('typography.by', { author: String(log.data?.accountEmail) })
      case ['domain_matched', 'email_on_trusted_school_domain'].includes(log.type) &&
        typeof log.data?.domain === 'string':
        return String(log.data?.domain)
      case ['expiry_set', 'expiry_extended'].includes(log.type) && typeof log.data?.expiresAt === 'string':
        return t('typography.agentVerificationTimeline.expiresAt', {
          date: format(new Date(String(log.data?.expiresAt)), 'd MMMM yyyy')
        })
      case log.type === 'blocked_by_policy' && typeof log.data?.blockPolicy === 'string':
        return t(`typography.agentVerificationTimeline.blockPolicy.${log.data.blockPolicy}`, {
          defaultValue: log.data.blockPolicy
        })
      case typeof log.data?.kycNotRequiredReason === 'string':
        return t(`typography.agentVerificationTimeline.kyc_verification_not_required.${log.data.kycNotRequiredReason}`)
      default:
        return undefined
    }
  }

  const getReusedAgentVerificationId = (log: AgentVerificationLog) =>
    REUSED_AGENT_VERIFICATION_LOG_TYPES.includes(log.type) && typeof log.data?.reusedAgentVerificationId === 'string'
      ? log.data.reusedAgentVerificationId
      : undefined

  if (!logs?.length) return null

  return [...logs]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .map(log => {
      const createdAtDate = new Date(log.createdAt)
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000)

      return {
        title: t(`typography.agentVerificationTimeline.${log.type}`),
        description: getDescription(log),
        reusedAgentVerificationId: getReusedAgentVerificationId(log),
        additionalDescription:
          createdAtDate < oneHourAgo
            ? format(createdAtDate, 'yyyy-MM-dd HH:mm')
            : formatDistanceToNow(createdAtDate, { addSuffix: true })
      }
    })
}
